import React, { useEffect } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import Galaxy3D from "@/components/Galaxy3D";

function GyroRing({ tilt, spin, reverse, border = "rgba(255,255,255,0.22)", glow = "rgba(255,138,0,0.18)", dotColor = "#fff" }) {
  return (
    <div
      className="absolute left-1/2 top-1/2 w-[145%] h-[145%]"
      style={{ transform: `translate(-50%,-50%) ${tilt}`, transformStyle: "preserve-3d" }}
    >
      <div
        className="w-full h-full rounded-full border"
        style={{
          borderColor: border,
          animation: `spin-slow ${spin}s linear infinite${reverse ? " reverse" : ""}`,
          boxShadow: `0 0 30px ${glow}, inset 0 0 50px rgba(255,61,119,0.1)`,
        }}
      >
        <span
          className="absolute -top-1 left-1/2 w-2 h-2 rounded-full"
          style={{ background: dotColor, boxShadow: `0 0 12px ${dotColor}` }}
        />
      </div>
    </div>
  );
}

export default function OrbBackground() {
  const { scrollY } = useScroll();
  const yGlow = useTransform(scrollY, (v) => v * -0.05);
  const ySphereA = useTransform(scrollY, (v) => v * -0.16);
  const ySphereB = useTransform(scrollY, (v) => v * -0.26);
  const yRing = useTransform(scrollY, (v) => v * -0.09);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const glowX = useTransform(sx, (v) => v * 0.4);
  const glowY = useTransform(sy, (v) => v * 0.4);
  const tiltX = useTransform(sy, (v) => v * -0.14);
  const tiltY = useTransform(sx, (v) => v * 0.1);

  useEffect(() => {
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 34);
      my.set((e.clientY / window.innerHeight - 0.5) * 22);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* True 3D layer — stars + wireframe dimensional structures (react-three-fiber) */}
      <Galaxy3D />

      {/* Tilted 3D scene — whole layer leans with the mouse like a parallel dimension */}
      <motion.div
        className="absolute inset-0"
        style={{ rotateX: tiltX, rotateY: tiltY, transformPerspective: 1400, transformStyle: "preserve-3d" }}
      >
        {/* Ambient glows — slow parallax layer */}
        <motion.div className="absolute inset-0" style={{ y: yGlow, x: glowX }}>
          <div
            className="absolute -top-48 left-1/2 -translate-x-1/2 w-[75rem] h-[42rem] rounded-full opacity-35"
            style={{ background: "radial-gradient(ellipse at center, rgba(14,116,144,0.35), transparent 65%)", filter: "blur(80px)" }}
          />
          <div
            className="absolute bottom-[-22%] right-[-12%] w-[42rem] h-[42rem] rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, rgba(123,47,247,0.4), transparent 65%)", filter: "blur(110px)" }}
          />
          <div
            className="absolute top-[32%] left-[-14%] w-[32rem] h-[32rem] rounded-full opacity-[0.16]"
            style={{ background: "radial-gradient(circle, rgba(255,61,119,0.35), transparent 65%)", filter: "blur(100px)" }}
          />
        </motion.div>

        {/* Giant distant orbit ring — deep space layer */}
        <motion.div className="absolute inset-0" style={{ y: yRing }}>
          <div className="absolute left-1/2 top-[4%] -translate-x-1/2 w-[64rem] h-[64rem]" style={{ perspective: "1000px" }}>
            <div
              className="w-full h-full rounded-full border border-white/[0.07]"
              style={{ transform: "rotateX(76deg)", animation: "spin-slow 60s linear infinite" }}
            />
          </div>
        </motion.div>

        {/* 3D perspective grid floor — receding into the horizon */}
        <div className="absolute inset-x-[-15%] bottom-[-4%] h-[38vh]" style={{ perspective: "620px" }}>
          <div className="absolute inset-0 grid-floor" style={{ transform: "rotateX(63deg)", transformOrigin: "50% 100%" }} />
        </div>

        {/* Sphere A — left bottom, gyroscope rings */}
        <motion.div className="absolute inset-0" style={{ y: ySphereA, x: sx, translateY: sy }}>
          <div
            className="absolute left-[-9%] bottom-[4%] w-72 h-72 md:w-[26rem] md:h-[26rem] orb-float"
            style={{ transformStyle: "preserve-3d", perspective: "900px" }}
          >
            <div
              className="absolute inset-0 rounded-full opacity-25 md:opacity-50"
              style={{
                background: "radial-gradient(circle at 32% 28%, #FFD37A 0%, #FF8A00 22%, #FF3D77 52%, #7B2FF7 78%, #170B2E 100%)",
                filter: "blur(2px)",
                boxShadow: "0 0 90px rgba(255,61,119,0.35)",
              }}
            />
            <GyroRing tilt="rotateX(74deg)" spin={24} />
            <GyroRing tilt="rotateX(74deg) rotateZ(58deg)" spin={34} reverse border="rgba(255,255,255,0.12)" glow="rgba(142,59,255,0.15)" dotColor="#FFC98A" />
          </div>
        </motion.div>

        {/* Sphere B — right mid, larger, crossed gyroscope rings */}
        <motion.div className="absolute inset-0" style={{ y: ySphereB, x: sx, translateY: sy }}>
          <div
            className="absolute right-[-16%] top-[40%] w-[24rem] h-[24rem] md:w-[36rem] md:h-[36rem] orb-float-late"
            style={{ transformStyle: "preserve-3d", perspective: "1100px" }}
          >
            <div
              className="absolute inset-0 rounded-full opacity-25 md:opacity-50"
              style={{
                background: "radial-gradient(circle at 28% 24%, #FFE29A 0%, #FF9E2C 26%, #FF4D6D 58%, #8E3BFF 84%, #12082A 100%)",
                filter: "blur(2px)",
                boxShadow: "0 0 110px rgba(255,138,0,0.3)",
              }}
            />
            <GyroRing tilt="rotateX(70deg) rotateZ(-14deg)" spin={32} reverse border="rgba(255,255,255,0.18)" glow="rgba(142,59,255,0.2)" dotColor="#FED7AA" />
            <GyroRing tilt="rotateX(70deg) rotateZ(52deg)" spin={44} border="rgba(255,255,255,0.1)" glow="rgba(255,61,119,0.12)" dotColor="#C4B5FD" />
          </div>

          <div
            className="absolute right-[7%] top-[8%] w-20 h-20 md:w-28 md:h-28 rounded-full orb-float-slow"
            style={{
              background: "radial-gradient(circle at 35% 30%, #FFC98A 0%, #FF8A00 30%, #E23A87 65%, #4C1D95 100%)",
              filter: "blur(5px)",
              opacity: 0.45,
            }}
          />
        </motion.div>

        {/* Soft white bokeh */}
        <motion.div className="absolute inset-0" style={{ x: glowX, y: glowY }}>
          <div className="absolute top-[16%] left-[5%] w-24 h-24 rounded-full bg-white/15" style={{ filter: "blur(34px)" }} />
          <div className="absolute bottom-[28%] right-[30%] w-16 h-16 rounded-full bg-white/10" style={{ filter: "blur(26px)" }} />
        </motion.div>
      </motion.div>

      {/* Central content scrim — keeps copy readable while orbs live at the edges */}
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse 60% 75% at 50% 44%, rgba(2,8,10,0.72), rgba(2,8,10,0.3) 58%, transparent 80%)" }}
      />

      {/* Vignette */}
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 260px rgba(0,0,0,0.9)" }} />
    </div>
  );
}
